<template>
    <div class="flex flex-col items-center select-none" v-if="props.visible" id="transpose">
        <div class="flex items-center justify-between w-full mb-2" v-if="props.showLabel">
            <span class="text-xs font-bold text-gray-700 dark:text-gray-200 flex items-center gap-1.5">
                Transpose
                <span v-if="pinInfo?.canPin" class="relative inline-flex group/pin">
                    <button
                        type="button"
                        @click="togglePin"
                        class="p-1 rounded-full transition-colors"
                        :class="pinInfo?.pinned != null
                            ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/60'
                            : 'text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300'"
                    >
                        <LockClosedIcon v-if="pinInfo?.pinned != null" class="w-3.5 h-3.5" />
                        <LockOpenIcon v-else class="w-3.5 h-3.5" />
                    </button>
                    <span class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/pin:block z-[300] pointer-events-none w-max max-w-[190px] whitespace-normal text-center text-[11px] font-medium leading-snug text-white bg-gray-800 dark:bg-white dark:text-slate-800 rounded-md px-2.5 py-1.5 shadow-lg">
                        {{ pinInfo?.pinned != null ? $t('tooltip.pinnedTone') : $t('tooltip.pinTone') }}
                    </span>
                </span>
            </span>
            <button 
                v-if="semitone !== 0"
                @click="reset"
                class="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
            >
                Reset
            </button>
        </div>
        <div class="flex items-center justify-between w-full bg-gray-50 dark:bg-slate-900 rounded-md p-1.5 border border-gray-200 dark:border-slate-700"
            :class="{ 'opacity-50': isBusy }">
            <button 
                type="button"
                @click="transpose(-1)"
                :disabled="isBusy"
                class="p-1.5 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors disabled:cursor-wait disabled:hover:bg-transparent"
                :title="isBusy ? 'Saving transpose...' : 'Transpose down (-1)'"
            >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M20 12H4"/>
                </svg>
            </button>
            <input 
                type="number"
                v-model.number="inputValue"
                @keydown="onKeyDown"
                @change="onInputChange"
                :disabled="isBusy"
                class="text-sm font-bold text-gray-900 dark:text-white bg-transparent w-12 text-center border-none outline-none focus:outline-none focus:ring-0 rounded px-1 disabled:cursor-wait"
            />
            <button 
                type="button"
                @click="transpose(1)"
                :disabled="isBusy"
                class="p-1.5 rounded-full text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors disabled:cursor-wait disabled:hover:bg-transparent"
                :title="isBusy ? 'Saving transpose...' : 'Transpose up (+1)'"
            >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/>
                </svg>
            </button>
        </div>

        <!-- Instrument Transposition & Capo Selector -->
        <div v-if="props.showLabel" class="w-full mt-2.5 pt-2.5 border-t border-gray-200 dark:border-slate-700 space-y-2">
            <div class="flex flex-col gap-1">
                <label class="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Instrumento
                </label>
                <select
                    data-instrument-select
                    :value="selectedInstrument"
                    @change="onInstrumentChange($event.target.value)"
                    class="w-full text-xs font-medium text-gray-800 dark:text-gray-100 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                    <option v-for="inst in instrumentList" :key="inst.id" :value="inst.id">
                        {{ inst.name }}
                    </option>
                </select>
            </div>
            <div class="flex flex-col gap-1" v-if="selectedInstrumentSupportsCapo">
                <label class="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Capotraste
                </label>
                <TriadSelect
                    data-capo-select
                    :model-value="selectedCapo"
                    :options="capoOptions"
                    root-class="block w-full"
                    button-class="bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-700 px-2 py-1.5 text-xs rounded-md"
                    menu-class="w-full min-w-[170px]"
                    aria-haspopup="listbox"
                    @change="onCapoChange"
                />
                <button
                    type="button"
                    data-show-capo-banner
                    @click="appEvents.emit('showCapoBanner')"
                    class="mt-0.5 text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline text-center focus:outline-none"
                >
                    {{ $t('stage.capoShow') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, onMounted, ref, watch, defineExpose, defineEmits, computed } from 'vue';
import transposeStore from '@/store/transpose.store';
import { appEvents } from '@/store/app.events';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/vue/24/outline';
import { INSTRUMENT_PROFILES, instrumentSupportsCapo } from '@/utils/instrumentTransposer';
import TriadSelect from '@/components/common/TriadSelect.vue';

// Desabilitado enquanto o persist do transpose está em voo (resposta do backend pendente).
const isBusy = computed(() => !!transposeStore.state.busy);
// Pino de tom do viewer (espelho vindo da PlaylistSongView).
const pinInfo = computed(() => transposeStore.state.transposePin);
const togglePin = () => {
    appEvents.emit('toggleTransposePin');
};

const instrumentList = Object.values(INSTRUMENT_PROFILES);
const selectedInstrument = computed(() => transposeStore.state.instrument || 'concert_c');
const selectedCapo = computed(() => Number(transposeStore.state.capo || 0));
const capoOptions = [
    { value: 0, label: 'Sem Capo' },
    ...Array.from({ length: 11 }, (_, i) => ({ value: i + 1, label: `Casa ${i + 1} (-${i + 1})` }))
];
// Capotraste só existe p/ instrumentos de corda dedilhada (violão/guitarra/etc).
const selectedInstrumentSupportsCapo = computed(() => instrumentSupportsCapo(selectedInstrument.value));

const onInstrumentChange = (instrumentId) => {
    transposeStore.state.instrument = instrumentId;
    // Instrumentos sem capotraste não devem herdar um capo antigo (evita shift fantasma).
    if (!instrumentSupportsCapo(instrumentId)) {
        transposeStore.state.capo = 0;
    }
    appEvents.emit('instrumentTranspositionChanged', {
        instrument: instrumentId,
        capo: Number(transposeStore.state.capo || 0)
    });
};

const onCapoChange = (capoFret) => {
    const fret = Number(capoFret) || 0;
    transposeStore.state.capo = fret;
    appEvents.emit('instrumentTranspositionChanged', {
        instrument: selectedInstrument.value,
        capo: fret
    });
};

const props = defineProps({
    visible: {
        type: Boolean,
        default: true
    },
    showLabel: {
        type: Boolean,
        default: true
    }
})

const semitone = ref(0);
const inputValue = ref(0);
const emits = defineEmits(['onTranspose'])

watch(semitone, (val) => {
    inputValue.value = val;
});

const transpose = (transposeValue, isInput = false) => {
    if (isBusy.value) return;
    let currentDelta = 0;
    if (isInput) {
        currentDelta = Number(transposeValue) - semitone.value;
        if (currentDelta === 0) return;
        semitone.value = Number(transposeValue);
    } else {
        currentDelta = Number(transposeValue);
        semitone.value = Number(semitone.value) + currentDelta;
    }
    emits('onTranspose', { up: currentDelta > 0, semitone: semitone.value, current: currentDelta });
    appEvents.emit('currentTranspose', { up: currentDelta > 0, semitone: semitone.value, current: currentDelta });
}

const onKeyDown = (event) => {
    if (event.key === 'Enter') {
        event.target.blur();
        return;
    }
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter']
    if (allowedKeys.includes(event.key)) return
    if (event.key === '-' || (event.key >= '0' && event.key <= '9')) return
    event.preventDefault()
}

const onInputChange = () => {
    const targetVal = Number(inputValue.value) || 0;
    transpose(targetVal, true);
}

const reset = () => {
    if (isBusy.value) return;
    const delta = -semitone.value;
    semitone.value = 0;
    emits('onTranspose', { up: delta > 0, semitone: 0, current: delta });
    appEvents.emit('currentTranspose', { up: delta > 0, semitone: 0, current: delta });
}

onMounted(() => {
    semitone.value = transposeStore.state.transpose;
    inputValue.value = semitone.value;
})

const updateSemitone = (value) => {
    semitone.value = Number(value);
    inputValue.value = Number(value);
}

appEvents.on('currentTranspose', (data) => {
    transposeStore.state.transpose = data.semitone;
    semitone.value = Number(data.semitone);
    inputValue.value = Number(data.semitone);
});

defineExpose({ updateSemitone })
</script>

<style scoped>
/* Hide number input spinners */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  border: none;
  outline: none;
}
</style>
